<script setup lang="ts">
/**
 * The one upload-target picker (AlexAmin/marcus#83).
 *
 * Mounted ONCE, in the app shell, and driven entirely by `useUploadTargetStore` — every upload entry
 * point in the app opens this same dialog through that store rather than growing a picker of its
 * own. See the store for why the resolution lives there and not at the five call sites.
 *
 * ── IT IS A CONFIRMATION, NOT AN INTERROGATION ──────────────────────────────────────────────────
 *
 * When the route already answers the question — a client page, a matter page, an open conversation —
 * the store pre-selects it and the user's job is to press the button. The full list is underneath
 * for the times the context is wrong or absent, and choosing a CLIENT is a complete answer: someone
 * who does not yet know which matter a paper belongs to can still say whose it is.
 *
 * NOTHING HAS BEEN UPLOADED WHEN THIS IS ON SCREEN, including for a drop: the store waits for an
 * answer before sending a byte (#83). So the secondary action is a plain "cancel" — it discards the
 * upload rather than leaving a document behind with no home, which is the state this ticket
 * abolishes and which the old "later" quietly produced. The cost is that a cancelled drop has to be
 * re-dropped; the file never left the user's disk, so nothing is lost.
 */
import { Building2, Briefcase, MessagesSquare, ChevronRight, Loader2, Archive } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { Button } from '~/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '~/components/ui/dialog'
import { useCasesStore } from '~/stores/cases'
import { useClientsStore } from '~/stores/clients'
import { useWebChatStore } from '~/stores/web-chat'
import { useUploadTargetStore, type UploadTarget } from '~/stores/uploadTarget'
import SearchInput from '~/components/controls/SearchInput.vue'

const { t } = useI18n()
const uploadTarget = useUploadTargetStore()
const { open, selected, noClientAvailable, busy, error, errorNames, count, names } = storeToRefs(uploadTarget)
const clientsStore = useClientsStore()
const casesStore = useCasesStore()
const webChat = useWebChatStore()

const query = ref('')
const expanded = ref<Set<string>>(new Set())

/**
 * Load the two lists when the dialog opens, not on mount.
 *
 * The shell only loads clients and cases in grouped mode, so the folder-tree user would otherwise
 * open an empty picker. Both stores dedupe into their own caches, so re-asking is cheap.
 */
watch(open, (isOpen) => {
  if (!isOpen) return
  query.value = ''
  // Reset manual expansion; `isExpanded` still opens whichever client holds the pre-selected case.
  expanded.value = new Set()
  if (!clientsStore.loaded) void clientsStore.listClients()
  if (!casesStore.loaded) void casesStore.listCases()
})

const conversationTarget = computed<Extract<UploadTarget, { kind: 'conversation' }> | null>(() =>
  webChat.conversationId
    ? { kind: 'conversation', id: webChat.conversationId, name: webChat.conversationTitle ?? '' }
    : null)

/** Clients matching the search, each with the cases that match it too. A client whose NAME matches
 *  keeps all of its cases, so narrowing to a client still shows the matters inside it. */
const rows = computed(() => {
  const q = query.value.trim().toLowerCase()
  return clientsStore.list
    .map((client) => {
      const cases = casesStore.forClient(client.id)
      const clientHit = !q || client.name.toLowerCase().includes(q)
      const visibleCases = clientHit ? cases : cases.filter(c => c.name.toLowerCase().includes(q))
      return { client, cases: visibleCases, show: clientHit || visibleCases.length > 0 }
    })
    .filter(r => r.show)
})

function isSelected(kind: UploadTarget['kind'], id?: string): boolean {
  const s = selected.value
  if (!s || s.kind !== kind) return false
  return s.kind === 'noClient' || s.id === id
}

function toggle(clientId: string) {
  const next = new Set(expanded.value)
  if (next.has(clientId)) next.delete(clientId)
  else next.add(clientId)
  expanded.value = next
}

/** A client is expanded when the user opened it, or when the selected case lives inside it. */
function isExpanded(clientId: string): boolean {
  if (expanded.value.has(clientId)) return true
  const s = selected.value
  if (s?.kind !== 'case') return false
  return casesStore.forClient(clientId).some(c => c.id === s.id)
}

/**
 * A batch that was ENTIRELY disallowed types (#804) queues nothing — there is no file to pick a
 * home for, so `count` is 0 and the client/matter picker below has nothing to do. The dialog still
 * opens, but as a plain notice: title, the rejected names, and Cancel. `error` stays `'unsupported'`
 * for the whole time it is up (see `choose` in the store), so this can key off `error` alone rather
 * than trying to tell "opened for a real pick" and "opened only to say no" apart some other way.
 */
const nothingToFile = computed(() => count.value === 0 && error.value === 'unsupported')

const title = computed(() => {
  if (nothingToFile.value) return t('uploadTarget.titleUnsupported')
  return count.value > 1 ? t('uploadTarget.titlePlural', { n: count.value }) : t('uploadTarget.title')
})
</script>

<template>
  <!-- No type annotation in the inline handler: a template expression compiles as plain JS, so
       `(v: boolean) =>` is a syntax error that only surfaces on a full SSR page load. -->
  <Dialog :open="open" @update:open="v => { if (!v) uploadTarget.dismiss() }">
    <DialogContent class="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
        <DialogDescription v-if="!nothingToFile">
          {{ noClientAvailable ? t('uploadTarget.descriptionNoClients') : t('uploadTarget.description') }}
        </DialogDescription>
      </DialogHeader>

      <template v-if="!nothingToFile">
        <!-- What is being filed. Named, because "1 document" is not enough to choose a home for it. -->
        <p class="text-xs text-muted-foreground truncate">{{ names.join(', ') }}</p>

        <!-- A plan without clients has nothing to search (MAR-1006). -->
        <SearchInput v-if="!noClientAvailable" v-model="query" :placeholder="t('uploadTarget.search')" size="sm" />
      </template>

      <div v-if="!nothingToFile" class="max-h-72 overflow-y-auto -mx-1 px-1">
        <!-- The open thread, first: on the chat surface it is usually the answer. -->
        <button
          v-if="conversationTarget"
          type="button"
          class="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-accent-lighter transition-colors"
          :class="isSelected('conversation', conversationTarget.id) && 'bg-accent-lighter ring-1 ring-inset ring-primary'"
          @click="uploadTarget.choose(conversationTarget)"
        >
          <MessagesSquare class="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span class="flex-1 min-w-0 truncate">
            {{ t('uploadTarget.conversation') }}
            <span v-if="conversationTarget.name" class="text-muted-foreground">· {{ conversationTarget.name }}</span>
          </span>
        </button>

        <!-- A plan without case management cannot create a client, so a document is filed with no
             client: attached to nothing, shown under "Sin cliente" (MAR-1006). Offered on that plan only. -->
        <button
          v-if="noClientAvailable"
          type="button"
          class="w-full flex items-start gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-accent-lighter transition-colors"
          :class="isSelected('noClient') && 'bg-accent-lighter ring-1 ring-inset ring-primary'"
          :aria-pressed="isSelected('noClient')"
          @click="uploadTarget.choose({ kind: 'noClient' })"
        >
          <Archive class="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
          <span class="flex-1 min-w-0">
            <span class="block truncate">{{ t('uploadTarget.noClient') }}</span>
            <span class="block text-xs text-muted-foreground">{{ t('uploadTarget.noClientHint') }}</span>
          </span>
        </button>

        <div v-for="row in rows" :key="row.client.id">
          <div
            class="w-full flex items-center gap-1 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-accent-lighter transition-colors"
            :class="isSelected('client', row.client.id) && 'bg-accent-lighter ring-1 ring-inset ring-primary'"
            role="button"
            @click="uploadTarget.choose({ kind: 'client', id: row.client.id, name: row.client.name })"
          >
            <button
              type="button"
              class="h-4 w-4 flex items-center justify-center shrink-0 text-muted-foreground"
              :class="!row.cases.length && 'invisible'"
              :aria-expanded="isExpanded(row.client.id)"
              @click.stop="toggle(row.client.id)"
            >
              <ChevronRight
                class="h-3.5 w-3.5 transition-transform duration-150"
                :class="isExpanded(row.client.id) && 'rotate-90'"
              />
            </button>
            <Building2 class="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span class="flex-1 min-w-0 truncate">{{ row.client.name }}</span>
          </div>

          <button
            v-for="c in (isExpanded(row.client.id) ? row.cases : [])"
            :key="c.id"
            type="button"
            class="w-full flex items-center gap-2 pl-9 pr-2 py-1.5 rounded-md text-left text-sm hover:bg-accent-lighter transition-colors"
            :class="isSelected('case', c.id) && 'bg-accent-lighter ring-1 ring-inset ring-primary'"
            @click="uploadTarget.choose({ kind: 'case', id: c.id, name: c.name })"
          >
            <Briefcase class="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span class="flex-1 min-w-0 truncate">{{ c.name }}</span>
          </button>
        </div>

        <p v-if="!rows.length && !conversationTarget && !noClientAvailable" class="px-2 py-6 text-center text-sm text-muted-foreground">
          {{ t('uploadTarget.noClients') }}
        </p>
      </div>

      <p v-if="error === 'upload'" class="text-sm text-destructive">{{ t('uploadTarget.uploadFailed') }}</p>
      <p v-else-if="error === 'link'" class="text-sm text-destructive">
        {{ t('uploadTarget.linkFailed', { names: errorNames.join(', ') }) }}
      </p>
      <p v-else-if="error === 'unsupported'" class="text-sm text-destructive">
        {{ t('uploadTarget.unsupportedTypes', { names: errorNames.join(', ') }) }}
      </p>

      <DialogFooter class="flex-wrap gap-2">
        <Button variant="ghost" :disabled="busy" @click="uploadTarget.dismiss()">
          {{ t('common.cancel') }}
        </Button>
        <Button v-if="!nothingToFile" :disabled="!selected || busy" @click="uploadTarget.confirm()">
          <Loader2 v-if="busy" class="h-3.5 w-3.5 mr-1.5 animate-spin" />
          {{ t('uploadTarget.save') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
